import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Folder, Zap, Calendar, User, LogOut, Target, FileText } from 'react-feather';
import BankIntegrationPage from './settings/BankIntegrationPage';
import AccountPage from './settings/AccountPage';
import RulesPage from './settings/RulesPage';
import RulesInfoPage from './settings/RulesInfoPage';
import CategoriesPage from './settings/CategoriesPage';
import PlannedExpensesPage from './settings/PlannedExpensesPage';
import ReceiptsPage from './settings/ReceiptsPage';
import SavingsGoalsPage from './settings/SavingsGoalsPage';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isSubPage = location.pathname !== '/settings';

  // Render sub-pages
  if (isSubPage) {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    const secondLastPart = pathParts[pathParts.length - 2];
    
    // Handle nested routes like /settings/rules/info
    if (secondLastPart === 'rules' && lastPart === 'info') {
      return <RulesInfoPage />;
    }
    
    switch (lastPart) {
      case 'bank-integration':
        return <BankIntegrationPage />;
      case 'account':
        return <AccountPage />;
      case 'rules':
        return <RulesPage />;
      case 'categories':
        return <CategoriesPage />;
      case 'goals':
        return <SavingsGoalsPage />;
      case 'planned-expenses':
        return <PlannedExpensesPage />;
      case 'receipts':
        return <ReceiptsPage />;
      default:
        return <div>Page not found</div>;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Inställningar</h1>
          </div>
        </div>
      </div>
      
      <div className="page-content px-4 py-6">

        {/* Bank Integration Section */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Bankintegration
          </h2>
          <div 
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/settings/bank-integration')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <CreditCard size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Anslut din bank</h3>
                  <p className="text-sm text-gray-500">Import transaktioner automatiskt</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>
        </div>

        {/* Budget & Categories Section */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Budget & kategorier
          </h2>
          
          <div 
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors mb-3"
            onClick={() => navigate('/settings/categories')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <Folder size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Kategorier</h3>
                  <p className="text-sm text-gray-500">Hantera utgiftskategorier och budgetar</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors mb-3"
            onClick={() => navigate('/settings/goals')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <Target size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Sparmål</h3>
                  <p className="text-sm text-gray-500">Kassor för semester, presenter och större inköp</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors mb-3"
            onClick={() => navigate('/settings/receipts')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <FileText size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Kvitton</h3>
                  <p className="text-sm text-gray-500">Hämta kvitton från e-posten och stäm av mot utläggen</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors mb-3"
            onClick={() => navigate('/settings/rules')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <Zap size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Regler</h3>
                  <p className="text-sm text-gray-500">Automatisk kategorisering av transaktioner</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>

          <div 
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/settings/planned-expenses')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <Calendar size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Planerade utgifter</h3>
                  <p className="text-sm text-gray-500">Kommande betalningar och återkommande utgifter</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>
        </div>

        {/* Account Section */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Konto
          </h2>
          
          <div 
            className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/settings/account')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <User size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Kontohantering</h3>
                  <p className="text-sm text-gray-500">Kontoinformation och inställningar</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg p-3 transition-colors"
          >
            <LogOut size={20} className="mr-3" />
            <span className="text-base font-semibold">Logga ut</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;